import { useEffect, useState } from 'react';
import { Spin, Tag, Empty } from 'antd';
import { getCategoryPermissions } from '../../api/adminAPI';
import type {
    PermissionBoard,
    PermissionStatus,
    BoardCategory,
} from './hooks/useUserBoardPermissions';

interface UserPermissionDetailProps {
    userId: number;
    permBoards: PermissionBoard[];
    statusByBoard: Record<number, PermissionStatus>;
    categoriesByBoard: Record<number, BoardCategory[]>;
}

/**
 * 유저 행을 펼쳤을 때 표시하는 상세 — 게시판별로 권한을 가진 카테고리 이름 목록.
 * NONE 이 아닌 게시판에 대해서만 카테고리 권한을 개별 조회한다(펼칠 때만 호출).
 */
const UserPermissionDetail = ({
    userId,
    permBoards,
    statusByBoard,
    categoriesByBoard,
}: UserPermissionDetailProps) => {
    const [permittedByBoard, setPermittedByBoard] = useState<Record<number, number[]>>({});
    const [loading, setLoading] = useState(true);

    const activeBoards = permBoards.filter(
        (b) => (statusByBoard[b.boardId] || 'NONE') !== 'NONE',
    );

    useEffect(() => {
        let cancelled = false;

        const fetchDetails = async () => {
            setLoading(true);
            const result: Record<number, number[]> = {};
            await Promise.all(
                activeBoards.map(async (board) => {
                    try {
                        const res = await getCategoryPermissions(userId, board.rootCategoryId);
                        const data = res?.data || [];
                        result[board.boardId] = data
                            .filter((item: any) => item.hasPermission === true)
                            .map((item: any) => item.categoryId);
                    } catch {
                        result[board.boardId] = [];
                    }
                }),
            );
            if (!cancelled) {
                setPermittedByBoard(result);
                setLoading(false);
            }
        };

        fetchDetails();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    if (activeBoards.length === 0) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="권한이 부여된 게시판이 없습니다."
                style={{ margin: '8px 0' }}
            />
        );
    }

    if (loading) {
        return (
            <div style={{ padding: 16, textAlign: 'center' }}>
                <Spin size="small" />
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 8px' }}>
            {activeBoards.map((board) => {
                const permittedIds = permittedByBoard[board.boardId] || [];
                const nameById = new Map(
                    (categoriesByBoard[board.boardId] || []).map((c) => [c.id, c.name]),
                );
                return (
                    <div key={board.boardId}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>
                            {board.label}
                            <span style={{ color: '#999', fontWeight: 400, marginLeft: 8 }}>
                                {permittedIds.length}개 카테고리
                            </span>
                        </div>
                        {permittedIds.length === 0 ? (
                            <span style={{ color: '#bbb' }}>권한 없음</span>
                        ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {permittedIds.map((id) => (
                                    <Tag key={id} color="green" style={{ margin: 0 }}>
                                        {nameById.get(id) ?? id}
                                    </Tag>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default UserPermissionDetail;
